import { create } from 'zustand';
import type { AccessRule, PayMethod, Role, StoreStatus } from '@sense/shared';
import { api } from './api';
import { db, getMeta, migrateCounters, setMeta } from './db';
import { firebaseIdToken, getFirebaseAuth } from './firebase';

export interface StoreSettings {
  receiptFooter?: string; dailyTarget?: number; upiId?: string;
  billTemplate?: string; billStart?: number; allowPriceOverride?: boolean; maxDiscountPct?: number;
  rolePermissions?: Partial<Record<Role, string[]>>;
  payMethods?: PayMethod[]; defaultPay?: PayMethod; upiName?: string;
}
export type BusinessType = 'supermarket' | 'grocery' | 'fashion' | 'electronics' | 'bakery' | 'other';
export interface Me {
  user: { id: string; name: string; role: Role; empCode: string | null; access: AccessRule | null };
  storeCount: number;
  tenant: { id: string; name: string; plan_id: string; status: string; trial_ends_at: number | null; catalog_version: number };
  store: {
    id: string; code: string; name: string; address: string | null; phone: string | null; gstin: string | null; settings: StoreSettings;
    store_no: string; business_type: BusinessType; setup_steps: string[]; kyc_status: string;
    status: StoreStatus; submitted_at: number | null; review_note: string | null; kyc_docs: string[];
  };
  device: { id: string; code: string };
}

type Status = 'loading' | 'signedOut' | 'onboarding' | 'ready' | 'admin';
interface Saved { token: string; method: 'firebase' | 'demo' | 'employee'; email?: string; code?: string; admin?: boolean }

interface SessionState {
  status: Status;
  token: string | null;
  onboardingToken: string | null;
  suggestedName: string | null;
  me: Me | null;
  init(): Promise<void>;
  signIn(idToken: string, method: 'firebase' | 'demo', email?: string): Promise<void>;
  signInEmployee(code: string, password: string): Promise<void>;
  onboard(form: { storeName: string; ownerName: string; businessType: BusinessType; sampleData: boolean }): Promise<string>;
  switchStore(storeId: string): Promise<void>;
  refresh(): Promise<boolean>;
  loadMe(): Promise<void>;
  signOut(): Promise<void>;
}

const saved = (): Saved | null => {
  try {
    return JSON.parse(localStorage.getItem('sd.session') ?? 'null');
  } catch {
    return null;
  }
};

interface SessionResponse { onboarding: boolean; token: string; deviceId?: string; deviceCode?: string; storeId?: string; storeStatus?: StoreStatus; name?: string | null; storeNo?: string; platformAdmin?: boolean }

/** Every terminal this browser holds (one per store); the server reuses the one for the store it opens. */
async function knownDevices(): Promise<string[]> {
  return Object.values(await getMeta<Record<string, string>>('devices', {}));
}

async function exchange(idToken: string): Promise<SessionResponse> {
  return api<SessionResponse>('/auth/session', { method: 'POST', json: { idToken, deviceIds: await knownDevices() }, token: '' });
}

async function rememberDevice(r: SessionResponse) {
  if (!r.deviceId || !r.storeId) return;
  const map = await getMeta<Record<string, string>>('devices', {});
  delete map.legacy;
  await setMeta('devices', { ...map, [r.storeId]: r.deviceId });
  await setMeta('deviceId', r.deviceId); // counters are keyed by device, so nothing resets here
}

/** Data that belongs to one store. Bills are uploaded before a switch, so nothing is lost. */
export async function clearStoreData() {
  await db.transaction('rw', [db.products, db.offers, db.suppliers, db.customers, db.batches, db.bills, db.stock, db.staff, db.held, db.shifts, db.ledger, db.meta], async () => {
    await Promise.all([db.products, db.offers, db.suppliers, db.customers, db.batches, db.bills, db.stock, db.staff, db.held, db.shifts, db.ledger].map((t) => t.clear()));
    await db.meta.bulkDelete(['me', 'stockAt', 'staffAt', 'catalogVersion', 'customersVersion', 'phoneSeeded']);
  });
}

/**
 * Swap the session for a fresh one before it expires (12 h). It also carries the latest role,
 * store status and access rules, so changes reach the till without signing in again.
 */
export async function renew(): Promise<boolean> {
  const s = saved();
  if (!s?.token || s.admin) return false;
  try {
    const r = await api<SessionResponse>('/api/session/refresh', { method: 'POST', json: {} });
    if (!r.token) return false; // never replace a working session with an empty one
    localStorage.setItem('sd.session', JSON.stringify({ ...s, token: r.token }));
    useSession.setState({ token: r.token });
    return true;
  } catch {
    return false;
  }
}
setInterval(() => { const st = useSession.getState(); if (st.status === 'ready' && st.me?.tenant.plan_id !== 'starter') void renew(); }, 3 * 3600_000);

export const useSession = create<SessionState>((set, get) => ({
  status: 'loading',
  token: null,
  onboardingToken: null,
  suggestedName: null,
  me: null,

  async init() {
    await migrateCounters();
    const s = saved();
    if (s?.admin && s.token) { set({ token: s.token, status: 'admin' }); return; }
    const me = await getMeta<Me | null>('me', null);
    if (s?.token && me) {
      // Offline-first: open the till with the cached profile, refresh in the background.
      set({ token: s.token, me, status: 'ready' });
      // Phone-only (Starter) stores make no background calls; the plan page checks the plan on demand.
      if (me.tenant.plan_id !== 'starter') void renew().then(() => get().loadMe()).catch(() => {});
      return;
    }
    if (s?.token) {
      // Signed in but no cached profile yet (e.g. just switched store): fetch it.
      set({ token: s.token });
      try {
        await get().loadMe();
        set({ status: 'ready' });
        return;
      } catch { /* fall through to sign-in */ }
    }
    set({ status: 'signedOut' });
  },

  async signIn(idToken, method, email) {
    const r = await exchange(idToken);
    if (r.platformAdmin) {
      localStorage.setItem('sd.session', JSON.stringify({ token: r.token, method, email, admin: true } satisfies Saved));
      set({ token: r.token, status: 'admin' });
      return;
    }
    if (r.onboarding) {
      set({ status: 'onboarding', onboardingToken: r.token, suggestedName: r.name ?? null });
      localStorage.setItem('sd.pending', JSON.stringify({ method, email }));
      return;
    }
    await rememberDevice(r);
    localStorage.setItem('sd.session', JSON.stringify({ token: r.token, method, email } satisfies Saved));
    set({ token: r.token });
    await get().loadMe();
    set({ status: 'ready' });
  },

  async signInEmployee(code, password) {
    const r = await api<SessionResponse>('/auth/employee', { method: 'POST', json: { code: code.trim(), password, deviceIds: await knownDevices() }, token: '' });
    await rememberDevice(r);
    localStorage.setItem('sd.session', JSON.stringify({ token: r.token, method: 'employee', code: code.trim() } satisfies Saved));
    localStorage.setItem('sd.lastEmpCode', code.trim());
    set({ token: r.token });
    await get().loadMe();
    set({ status: 'ready' });
  },

  async switchStore(storeId) {
    const r = await api<SessionResponse>('/api/switch-store', { method: 'POST', json: { storeId, deviceIds: await knownDevices() } });
    await rememberDevice(r);
    const s = saved();
    localStorage.setItem('sd.session', JSON.stringify({ ...s, token: r.token }));
    // Every store has its own data (catalog, stock, customers, suppliers, bills): start clean.
    await clearStoreData();
    localStorage.removeItem('sd.cart');
    location.assign('/');
  },

  async onboard(form) {
    const r = await api<SessionResponse>('/onboard', { method: 'POST', json: form, token: get().onboardingToken ?? '' });
    await rememberDevice(r);
    const pending = JSON.parse(localStorage.getItem('sd.pending') ?? '{}');
    localStorage.setItem('sd.session', JSON.stringify({ token: r.token, method: pending.method ?? 'demo', email: pending.email }));
    localStorage.removeItem('sd.pending');
    set({ token: r.token, onboardingToken: null });
    await get().loadMe();
    set({ status: 'ready' });
    return r.storeNo ?? '';
  },

  async refresh() {
    const s = saved();
    if (!s || s.admin || s.method === 'employee') return false; // employees sign in again with their password
    try {
      const idToken = s.method === 'demo' ? `dev:${s.email ?? ''}` : await firebaseIdToken();
      if (!idToken) return false;
      const r = await exchange(idToken);
      if (r.onboarding || !r.token) return false;
      await rememberDevice(r);
      localStorage.setItem('sd.session', JSON.stringify({ ...s, token: r.token }));
      set({ token: r.token });
      return true;
    } catch {
      return false;
    }
  },

  async loadMe() {
    const me = await api<Me>('/api/me');
    await setMeta('me', me);
    set({ me });
  },

  async signOut() {
    const s = saved();
    if (s?.method === 'firebase') {
      const auth = await getFirebaseAuth().catch(() => null);
      await auth?.signOut();
    }
    localStorage.removeItem('sd.session');
    await db.meta.delete('me');
    set({ status: 'signedOut', token: null, me: null });
  },
}));

export const canManage = (role?: Role | null) => role === 'owner' || role === 'manager' || role === 'stock_keeper';
